import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  Clock3,
  Link2,
  Users,
  Bell,
  MoreHorizontal,
  Package,
  Map,
  Compass,
} from "lucide-react";

import { apiFetch } from "../utils/apiClient";
import {
  PACKAGE_TIER_EVENT,
  PACKAGE_TIER_STORAGE_KEY,
  determineHighestTier,
  readStoredTier,
  storeTierForUser,
} from "../utils/packageTier";

const navItems = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: LayoutGrid,
    path: "/dashboard",
    minTier: 1,
  },
  {
    key: "transactions",
    label: "Transaction History",
    icon: Clock3,
    path: "/transactions",
    minTier: 1,
  },
  {
    key: "packages",
    label: "Packages",
    icon: Package,
    path: "/dashboard/packages",
    minTier: 0,
  },
  {
    key: "referrer",
    label: "Referrer Link",
    icon: Link2,
    path: "/referrer-link",
    minTier: 2,
  },
  {
    key: "community",
    label: "Community",
    icon: Users,
    path: "/community",
    minTier: 2,
  },
  {
    key: "rewards",
    label: "Program",
    icon: Compass,
    path: "/rewards",
    minTier: 1,
  },
  {
    key: "roadmap",
    label: " Investors Guide",
    icon: Map,
    path: "/roadmap",
    minTier: 1,
  },
  {
    key: "notifications",
    label: "Notification",
    icon: Bell,
    path: "/notifications",
    minTier: 1,
  },
];

const useOutsideClick = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref?.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

const getInitials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "SC";

export const getDisplayName = (user) => {
  if (!user) return "Investor";
  return (
    user.name ||
    user.fullName ||
    user.username ||
    (user.email ? user.email.split("@")[0] : "Investor")
  );
};

export const getRoleLabel = (user) => {
  if (!user) return "Affiliate";
  return user.role || user.accountType || user.tier || "Affiliate";
};

export const getAvatarData = (user, displayName) => {
  const avatarUrl =
    user?.avatarUrl || user?.avatar || user?.profileImage || user?.image;
  return {
    avatarUrl: avatarUrl || null,
    initials: getInitials(displayName),
  };
};

export default function DashboardLayout({
  user,
  onSignOut,
  activeKey = "dashboard",
  children,
}) {
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const resolvedUserId =
    user?.id || (typeof user?._id === "string" && user?._id) || null;
  const propTier = Number(user?.packageTier);
  const initialTier =
    readStoredTier(resolvedUserId) ??
    (Number.isFinite(propTier) ? Math.max(0, propTier) : 0);
  const [packageTier, setPackageTier] = useState(
    () => initialTier,
  );
  const sidebarMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const closeSidebarMenu = useCallback(() => setSidebarMenuOpen(false), []);
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const UNREAD_STORAGE_KEY = "synergycoin:unreadNotifications";

  useOutsideClick(sidebarMenuRef, closeSidebarMenu);
  useOutsideClick(mobileMenuRef, closeMobileMenu);

  const syncUnreadToStorage = (count) => {
    if (typeof window === "undefined") {
      return;
    }

    const normalized = Math.max(Number.isFinite(count) ? count : 0, 0);

    try {
      window.localStorage.setItem(UNREAD_STORAGE_KEY, String(normalized));
      window.dispatchEvent(
        new CustomEvent("synergy:unread-updated", { detail: normalized }),
      );
    } catch (error) {
      // ignore storage access issues
    }
  };

  useEffect(() => {
    const readInitial = () => {
      try {
        const stored = window.localStorage.getItem(UNREAD_STORAGE_KEY);
        if (stored) {
          const parsed = Number(stored);
          setUnreadNotifications(Number.isFinite(parsed) ? Math.max(parsed, 0) : 0);
        } else {
          setUnreadNotifications(0);
        }
      } catch (error) {
        setUnreadNotifications(0);
      }
    };

    const handleStorage = (event) => {
      if (event.key && event.key !== UNREAD_STORAGE_KEY) return;
      readInitial();
    };

    const handleCustomEvent = (event) => {
      const count = Number(event.detail);
      setUnreadNotifications(Number.isFinite(count) ? Math.max(count, 0) : 0);
    };

    readInitial();
    window.addEventListener("storage", handleStorage);
    window.addEventListener("synergy:unread-updated", handleCustomEvent);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("synergy:unread-updated", handleCustomEvent);
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const fetchUnreadCount = async () => {
      if (!user?.id) {
        if (!ignore) {
          setUnreadNotifications(0);
          syncUnreadToStorage(0);
        }
        return;
      }

      try {
        const response = await apiFetch("/notifications?status=unread", {
          headers: { "x-user-id": user.id },
        });

        if (ignore) return;

        const unread = Number(response?.unread);
        const count = Number.isFinite(unread)
          ? unread
          : Array.isArray(response?.notifications)
            ? response.notifications.length
            : 0;

        setUnreadNotifications(Math.max(count, 0));
        syncUnreadToStorage(count);
      } catch (error) {
        if (!ignore) {
          // leave existing count; optionally reset to last stored value
        }
      }
    };

    fetchUnreadCount();

    return () => {
      ignore = true;
    };
  }, [user?.id]);

  const displayName = useMemo(() => getDisplayName(user), [user]);
  const roleLabel = useMemo(() => getRoleLabel(user), [user]);
  const { avatarUrl, initials } = useMemo(
    () => getAvatarData(user, displayName),
    [user, displayName],
  );

  const handleSignOut = () => {
    closeSidebarMenu();
    closeMobileMenu();
    if (onSignOut) {
      onSignOut();
    }
    setUnreadNotifications(0);
    syncUnreadToStorage(0);
  };

  const handleProfileNavigation = useCallback(() => {
    navigate("/profile");
    closeSidebarMenu();
    closeMobileMenu();
  }, [navigate, closeSidebarMenu, closeMobileMenu]);

  const context = {
    user,
    displayName,
    roleLabel,
    avatarUrl,
    initials,
    unreadNotifications,
    packageTier,
    signOut: handleSignOut,
  };

  useEffect(() => {
    let ignore = false;

    const fetchTier = async () => {
      if (!resolvedUserId) {
        if (!ignore) {
          setPackageTier(0);
        }
        return;
      }

      try {
        const response = await apiFetch("/transactions");
        if (ignore) return;
        const transactions = Array.isArray(response?.transactions)
          ? response.transactions
          : [];
        const transactionsTier = determineHighestTier(transactions);
        setPackageTier((current) => {
          const nextTier = Math.max(current, transactionsTier);
          if (resolvedUserId && transactionsTier > current) {
            storeTierForUser(resolvedUserId, transactionsTier);
          }
          return nextTier;
        });
      } catch (error) {
        if (!ignore) {
          setPackageTier((current) =>
            Number.isFinite(current) ? current : 0,
          );
        }
      }
    };

    fetchTier();

    return () => {
      ignore = true;
    };
  }, [resolvedUserId]);

  useEffect(() => {
    const tierFromUser = Number(user?.packageTier);
    if (!Number.isFinite(tierFromUser)) {
      return;
    }
    if (tierFromUser <= packageTier) {
      return;
    }
    setPackageTier(tierFromUser);
    if (resolvedUserId) {
      storeTierForUser(resolvedUserId, tierFromUser);
    }
  }, [packageTier, resolvedUserId, user?.packageTier]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleTierEvent = (event) => {
      const { userId: targetUserId, tier } = event.detail || {};
      if (
        targetUserId &&
        resolvedUserId &&
        targetUserId !== resolvedUserId
      ) {
        return;
      }

      if (Number.isFinite(tier)) {
        setPackageTier(Math.max(0, tier));
      } else if (!targetUserId && !resolvedUserId) {
        setPackageTier(0);
      }
    };

    window.addEventListener(PACKAGE_TIER_EVENT, handleTierEvent);
    return () => {
      window.removeEventListener(PACKAGE_TIER_EVENT, handleTierEvent);
    };
  }, [resolvedUserId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleStorage = (event) => {
      if (event.key && event.key !== PACKAGE_TIER_STORAGE_KEY) {
        return;
      }
      if (!resolvedUserId) {
        setPackageTier(0);
        return;
      }
      const storedTier = readStoredTier(resolvedUserId);
      if (storedTier === null) {
        setPackageTier(0);
      } else {
        setPackageTier(storedTier);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [resolvedUserId]);

  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      const requiredTier = Number.isFinite(item.minTier) ? item.minTier : 0;
      return packageTier >= requiredTier;
    });
  }, [packageTier]);

  return (
    <div className="min-h-screen w-full bg-black text-white">
      <div className="flex w-full flex-col gap-8 px-5 py-6 md:flex-row md:px-0 md:pr-8 2xl:pr-12">
        {/* Sidebar */}
        <aside className="hidden w-[280px] shrink-0 md:block md:sticky md:top-6 md:self-start">
          <div className="relative h-[calc(100vh-48px)] overflow-hidden rounded-3xl border border-white/10 bg-[#0F0B14]">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_100%_at_0%_100%,rgba(123,44,235,0.35),transparent_60%)]" />
            <div className="relative z-10 flex h-full flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              <div className="px-6 pt-6 pb-3">
                <button
                  onClick={() => navigate("/")}
                  className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <img
                    src="/images/nav.png"
                    alt="SynergyCoin"
                    className="h-14 w-auto object-contain"
                  />
                </button>

                <div className="mt-5 text-sm text-white/60">Overview</div>
              </div>

              <nav className="mt-3 space-y-1 px-3">
                {visibleNavItems.map(({ key, label, icon: Icon, path }) => {
                  const active = key === activeKey;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        if (path) navigate(path);
                      }}
                      className={`group flex w-full items-center gap-2.5 rounded-2xl px-4 py-2 text-left text-sm transition-all ${
                        active
                          ? "bg-[#2A1F36] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]"
                          : "text-white/70 hover:bg-white/5"
                      }`}
                    >
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                          active ? "bg-[#56338F]" : "bg-white/5 group-hover:bg-white/10"
                        }`}
                      >
                        <Icon size={16} />
                      </div>
                      <span className="relative inline-flex items-center gap-2">
                        {label}
                        {key === "notifications" && unreadNotifications > 0 && (
                          <span className="inline-flex min-h-[20px] min-w-[20px] items-center justify-center rounded-full bg-[#FF6B6B] px-2 text-[11px] font-semibold text-white">
                            {unreadNotifications > 99 ? "99+" : unreadNotifications}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </nav>

              <div className="mt-auto p-4">
                <div className="relative" ref={sidebarMenuRef}>
                  <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={displayName}
                        className="h-10 w-10 rounded-full object-cover"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#6E3FCB]/60 text-sm font-semibold text-white">
                        {initials}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{displayName}</div>
                      <div className="text-[12px] text-white/60">{roleLabel}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSidebarMenuOpen((prev) => !prev)}
                      className="rounded-full p-1 text-white/60 transition hover:bg-white/10"
                      aria-label="Open user menu"
                    >
                      <MoreHorizontal size={18} />
                    </button>
                  </div>
                  {sidebarMenuOpen && (
                    <div className="absolute right-0 bottom-full z-20 mb-2 w-44 rounded-2xl border border-white/10 bg-[#1A1224] p-2 shadow-2xl">
                      <button
                        type="button"
                        onClick={handleProfileNavigation}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-white/80 transition hover:bg-white/10 hover:text-white"
                      >
                        Profile
                      </button>
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-red-200 transition hover:bg-red-500/10"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile header & navigation */}
        <div className="md:hidden relative z-[100]">
          <div
            className="relative mb-5 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#110B1A]/80 p-4 backdrop-blur z-[100]"
            ref={mobileMenuRef}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="h-11 w-11 flex-shrink-0 rounded-full object-cover ring-2 ring-white/10"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#6E3FCB]/60 text-base font-semibold text-white ring-2 ring-white/10">
                {initials}
              </div>
            )}
            <div className="flex-1 text-left">
              <div className="text-sm font-semibold">{displayName}</div>
              <div className="text-xs text-white/60">{roleLabel}</div>
            </div>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="rounded-full p-1.5 text-white/60 transition hover:bg-white/10"
              aria-label="Open user menu"
            >
              <MoreHorizontal size={18} />
            </button>

            {/* Mobile dropdown - opens downward (top-full instead of bottom-full) */}
            {mobileMenuOpen && (
              <div 
                className="absolute right-4 top-full z-[9999] mt-2 w-44 rounded-2xl border border-white/10 bg-[#1A1224] p-2 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                style={{ position: 'absolute', zIndex: 99999 }}
              >
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const nav = () => {
                      closeMobileMenu();
                      navigate("/community");
                    };
                    nav();
                  }}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-white/80 transition hover:bg-white/10 hover:text-white cursor-pointer active:bg-white/15 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Community
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const nav = () => {
                      closeMobileMenu();
                      navigate("/transactions");
                    };
                    nav();
                  }}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-white/80 transition hover:bg-white/10 hover:text-white cursor-pointer active:bg-white/15 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Transaction History
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const nav = () => {
                      closeMobileMenu();
                      handleProfileNavigation();
                    };
                    nav();
                  }}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-white/80 transition hover:bg-white/10 hover:text-white cursor-pointer active:bg-white/15 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Profile
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const nav = () => {
                      closeMobileMenu();
                      handleSignOut();
                    };
                    nav();
                  }}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-red-200 transition hover:bg-red-500/10 cursor-pointer active:bg-red-500/20 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>

        <main className="flex-1 relative z-0">
          {typeof children === "function" ? children(context) : children}
        </main>
      </div>
    </div>
  );
}
